if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super()
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        }
        this.mql = window.matchMedia('(min-width: 750px)')
        this.connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
        this.saveData = Boolean(this.connection?.saveData)
        this.slowConnection = ['slow-2g', '2g'].includes(this.connection?.effectiveType)
        this.warmRadius = this.saveData || this.slowConnection ? 1 : 2
        this._onScrollWarm = this.onScrollWarm.bind(this)

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this))

          const slider =
            this.elements.viewer.slider ||
            this.elements.viewer.querySelector('[id^="Slider-"]')
          if (slider) {
            slider.addEventListener('pointerdown', this.onPointerWarm.bind(this), {
              passive: true,
            })
            if (!this.saveData) {
              slider.addEventListener('scroll', this._onScrollWarm, { passive: true })
            }
          }

          this.scheduleWarmAfterFeaturedLoad()
        }

        if (!this.elements.thumbnails) return

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button')
          if (!button) return
          button.addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          )
        })
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic()
        }
      }

      getSlides() {
        if (!this.elements.viewer) return []
        return Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        )
      }

      getModal() {
        if (this._modal !== undefined) return this._modal
        const opener = this.querySelector('modal-opener[data-modal]')
        this._modal = opener
          ? document.querySelector(opener.getAttribute('data-modal'))
          : null
        return this._modal
      }

      getFeaturedGalleryImage() {
        if (!this.elements.viewer) return null
        const activeMedia =
          this.elements.viewer.querySelector('[data-media-id].is-active') ||
          this.elements.viewer.querySelector('[data-media-id]')
        if (!activeMedia) return null
        return activeMedia.querySelector('.product__modal-opener img, .product__media img')
      }

      scheduleWarmAfterFeaturedLoad() {
        const start = () => {
          if (this._featuredWarmStarted) return
          this._featuredWarmStarted = true

          const activeMedia =
            this.elements.viewer.querySelector('[data-media-id].is-active') ||
            this.elements.viewer.querySelector('[data-media-id]')
          if (activeMedia) {
            this.warmAdjacentMedia(activeMedia, this.warmRadius)
          }

          this.bindLightboxPrefetchOnGalleryLoad()

          if (!this.saveData && !this.slowConnection) {
            this.scheduleIdleWarm()
          }
        }

        const featuredImg = this.getFeaturedGalleryImage()
        if (!featuredImg || (featuredImg.complete && featuredImg.naturalWidth > 0)) {
          start()
          return
        }

        featuredImg.addEventListener('load', start, { once: true })
        featuredImg.addEventListener('error', start, { once: true })
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement
        if (currentElement) {
          this.warmAdjacentMedia(currentElement, this.warmRadius)
        }

        if (!this.elements.thumbnails || !currentElement) return
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        )
        if (thumbnail) this.setActiveThumbnail(thumbnail)
      }

      bindLightboxPrefetchOnGalleryLoad() {
        this.getSlides().forEach((slide) => this.watchSlideForLightboxPrefetch(slide))
      }

      watchSlideForLightboxPrefetch(slide) {
        const galleryImg = slide.querySelector('.product__modal-opener img, .product__media img')
        if (!galleryImg || galleryImg.dataset.lightboxPrefetchBound === 'true') return
        galleryImg.dataset.lightboxPrefetchBound = 'true'

        const prefetch = () => this.prefetchModalForSlide(slide)
        if (galleryImg.complete && galleryImg.naturalWidth > 0) {
          prefetch()
        } else {
          galleryImg.addEventListener('load', prefetch, { once: true })
        }
      }

      prefetchModalForSlide(slide) {
        if (!slide || this.saveData) return

        const mediaButton = slide.querySelector('button[data-media-id]')
        const modal = this.getModal()
        if (!mediaButton || !modal || typeof modal.prefetchMedia !== 'function') return

        modal.prefetchMedia(mediaButton.getAttribute('data-media-id'), 0, false)
      }

      onPointerWarm() {
        const slides = this.getSlides()
        if (!slides.length) return

        const activeMedia =
          this.elements.viewer.querySelector('[data-media-id].is-active') ||
          this.getSlideNearScroll(slides) ||
          slides[0]
        this.warmAdjacentMedia(activeMedia, this.warmRadius + (this.slowConnection ? 0 : 1))
      }

      onScrollWarm() {
        if (this._scrollWarmRaf) return
        this._scrollWarmRaf = requestAnimationFrame(() => {
          this._scrollWarmRaf = null
          const slides = this.getSlides()
          const nearSlide = this.getSlideNearScroll(slides)
          if (nearSlide) this.warmAdjacentMedia(nearSlide, this.warmRadius)
        })
      }

      getSlideNearScroll(slides) {
        const slider =
          this.elements.viewer.slider ||
          this.elements.viewer.querySelector('[id^="Slider-"]')
        if (!slider || !slides.length) return null

        const center = slider.scrollLeft + slider.clientWidth / 2
        let closest = slides[0]
        let closestDistance = Infinity

        for (const slide of slides) {
          const slideCenter = slide.offsetLeft + slide.clientWidth / 2
          const distance = Math.abs(slideCenter - center)
          if (distance < closestDistance) {
            closestDistance = distance
            closest = slide
          }
        }

        return closest
      }

      warmAdjacentMedia(activeMedia, radius = this.warmRadius) {
        if (!this.elements.viewer || !activeMedia) return

        const slides = this.getSlides()
        const index = slides.indexOf(activeMedia)
        if (index === -1) return

        for (let offset = -radius; offset <= radius; offset++) {
          const slideIndex = index + offset
          if (slideIndex < 0 || slideIndex >= slides.length) continue
          this.warmSlideImages(slides[slideIndex], true)
        }
      }

      warmSlideImages(slide, prefetchLightbox = false) {
        if (!slide) return

        slide.querySelectorAll('.product__modal-opener img, .product__media img').forEach((img) => {
          if (img.dataset.mediaWarmed !== 'true') {
            img.dataset.mediaWarmed = 'true'

            if (img.getAttribute('loading') === 'lazy') {
              img.loading = 'eager'
              img.removeAttribute('loading')
            }

            if (typeof img.decode === 'function') {
              img.decode().catch(() => {})
            }
          }

          // Desktop hover-zoom uses a separate hi-res URL; warm it with the gallery image.
          this.prefetchDesktopZoomSrc(img)

          if (prefetchLightbox) {
            this.watchSlideForLightboxPrefetch(slide)
            if (img.complete && img.naturalWidth > 0) {
              this.prefetchModalForSlide(slide)
            }
          }
        })
      }

      prefetchDesktopZoomSrc(img) {
        if (!img || this.saveData || !this.mql.matches) return
        const zoomSrc = img.getAttribute('data-zoom-src')
        if (!zoomSrc || img.dataset.zoomSrcWarmed === 'true') return
        img.dataset.zoomSrcWarmed = 'true'
        const probe = new Image()
        probe.decoding = 'async'
        probe.fetchPriority = 'low'
        probe.src = zoomSrc
      }

      scheduleIdleWarm() {
        const warmRemaining = () => {
          // Warm remaining gallery frames for swipe snappiness only.
          // Lightbox files prefetch when each gallery image actually loads.
          this.getSlides().forEach((slide) => this.warmSlideImages(slide, false))
        }

        if ('requestIdleCallback' in window) {
          requestIdleCallback(warmRemaining, { timeout: 2500 })
        } else {
          setTimeout(warmRemaining, 1500)
        }
      }

      setActiveMedia(mediaId, prepend) {
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`)
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active')
        })
        if (!activeMedia) {
          activeMedia = this.elements.viewer.querySelector('[data-media-id]')
          if (!activeMedia) return
        }
        activeMedia.classList.add('is-active')
        this.warmAdjacentMedia(activeMedia, this.warmRadius)

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia)
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${activeMedia.dataset.mediaId}"]`
            )
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail)
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages()
        }

        this.preventStickyHeader()
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft })
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' })
          }
        })
        this.playActiveMedia(activeMedia)

        if (!this.elements.thumbnails) return
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        )
        this.setActiveThumbnail(activeThumbnail)
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail ? activeThumbnail.dataset.mediaPosition : 1
        )
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'))
        thumbnail.querySelector('button')?.setAttribute('aria-current', true)
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft })
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img')
        if (!image || !this.elements.liveRegion || !window.accessibilityStrings?.imageAvailable) {
          return
        }

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false)
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          )
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true)
          }, 2000)
        }

        if (image.complete) {
          announce()
        } else {
          image.addEventListener('load', announce, { once: true })
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia()
        const deferredMedia = activeItem.querySelector('.deferred-media')
        if (deferredMedia) deferredMedia.loadContent(false)
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header')
        if (!this.stickyHeader) return
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return
        this.elements.viewer.slider.setAttribute('role', 'presentation')
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        )
      }
    }
  )
}
